import * as React from "react"
import { Container, Row, Col, Table, Spinner, Button } from "react-bootstrap"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import { toast } from "react-toastify"
import { FiRefreshCw } from "react-icons/fi"
import { FaArrowDown, FaArrowUp, FaPrint } from "react-icons/fa"
import FileSaver from "file-saver"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import TablePagination from "../TablePagination"
import PoolGridData from "../../api/Randoms/RandomsModel/PoolGridData"
import ColumnFilter from "../../api/Randoms/RandomsModel/ColumnFilter"
import PoolGridRow from "../../api/Randoms/RandomsModel/PoolGridRow"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import SearchField from "../../api/SearchField"
import SearchBar from "../SearchBar"
import AddPoolModal from "./AddPoolModal"
import PoolDetailsModal from "./Pools/PoolDetailsModal"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import { PoolGridGetObservable, PoolGridCsvGet, PoolGridPrint } from "../../api/Randoms/PoolGridGet"
import PoolReadyToRunUpdateObservable, { PoolReadyToRunItem } from "../../api/Randoms/PoolReadyToRunUpdate"
import FormatDate from "../../formatters/FormatDate"
import Checkbox from "../Checkbox"
import ActionButton from "../ActionButton"
import { connect } from "react-redux"
import { AppState}  from "../../AppState"
import { BuildUserAccess } from "../../authentication/BuildUserAccess"
import RandomNav from "./RandomNav"
import { Filter, Sort } from "../../api/GridRequest"
import RunCancelModal from "./Pools/RunCancelModal"
import { csvHeader } from "../../api/Randoms/ImportPersonnelTemplate"

interface RandomPoolsProps {
  canManagePools?: boolean
  canManagePersonnel?: boolean
  canManageConsortiumPools?: boolean
  isConsortium?: boolean
  isPortalAdmin?: boolean
}

interface RandomPoolsState {
  loading: boolean
  loadingCsv: boolean
  updatingReadyToRun: boolean
  data?: PoolGridData
  details?: PoolGridRow
  detailsModal: boolean
  modal: boolean
  readyToRunChanges: PoolReadyToRunItem[]
  selectedPrintRowIDs: number[]
  runPools: boolean
  cancelPools: boolean
}

interface PoolGridSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
  search: { field: string, text: string }
}

const PageSize = 10

const DefaultSearchFields: SearchField[] = [
  { ID: "All", Name: "All" },
  { ID: "ID", Name: "ID" },
  { ID: "Name", Name: "Name" }
]

class RandomPools extends React.PureComponent<RandomPoolsProps, RandomPoolsState> {
  state: RandomPoolsState = {
    loading: false,
    loadingCsv: false,
    updatingReadyToRun: false,
    detailsModal: false,
    modal: false,
    readyToRunChanges: [],
    selectedPrintRowIDs: [],
    runPools: false,
    cancelPools: false,
  }

  page$ = new BehaviorSubject<PoolGridSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: [],
    search: { field: "", text: "" }
  })
  pageSubscription?: Subscription
  csvSubscription?: Subscription
  printSubscription?: Subscription
  updateReadyToRunSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        PoolGridGetObservable(params.pageNumber, PageSize, params.sort, params.filters, params.search, !!this.props.isConsortium).pipe(
          map((result: PoolGridData) => ({ data: result, loading: false })))
      )
      )
    ).subscribe(
      (newState: { loading: boolean, data?: PoolGridData }) => {
        if (newState.data) {
          this.setState({ loading: newState.loading, data: newState.data, readyToRunChanges: [], selectedPrintRowIDs: [] })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.csvSubscription && this.csvSubscription.unsubscribe()
    this.printSubscription && this.printSubscription.unsubscribe()
    this.updateReadyToRunSubscription && this.updateReadyToRunSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  pageChanged = (pageNumber: number) => this.page$.next({ ...this.page$.value, pageNumber: pageNumber })

  sortGrid(column: string, order?: string) {
    if (column === "LastUpdate") {
      column = "LastMemberImport"
    }
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  onSearch = (field: string, text: string) => this.page$.next({ ...this.page$.value, pageNumber: 1, search: { field: field, text: text } })

  handlePrintClick() {
    const value = this.page$.value
    this.printSubscription && this.printSubscription.unsubscribe()
    this.printSubscription = PoolGridPrint(this.state.selectedPrintRowIDs, value.filters, value.search, !!this.props.isConsortium).subscribe(
      data => this.print(data),
      error => toast.error(error)
    )
  }

  print(base64pdf: string) {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handlePrintSelectAllRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPrintRowIDs = e.target.checked && this.state.data && this.state.data.Records ? this.state.data.Records.map(item => item.PoolID) : []
    this.setState({ selectedPrintRowIDs: selectedPrintRowIDs })
  }

  changePrintRowSelection = (poolID: number, checked: boolean) => {
    let selected = [...this.state.selectedPrintRowIDs].filter(item => item !== poolID)
    this.setState({ selectedPrintRowIDs: checked ? [...selected, poolID] : selected })
  }

  renderHeaderColumn = (columnName: string, title: string) => {
    const sort = this.page$.value.sort
    return <th>
      {this.state.data
        ? <ColumnHeaderFilter title={title}
          sort={sort.column === columnName ? sort.order === "asc" ? "asc" : "desc" : undefined}
          items={(this.state.data as ColumnFilter)[columnName as keyof ColumnFilter]}
          filteredItems={this.page$.value.filters.find(item => item.column === columnName)?.values || []}
          onSort={(order?: string) => this.sortGrid(columnName, order)}
          onFilter={(items: string[]) => this.filterGrid(columnName, items)} />
        : title.toUpperCase()}
    </th>
  }

  renderReadyToRunHeaderColumn = () => {
    const sort = this.page$.value.sort
    const isReadyToRun = sort.column === "ReadyToRun"
    const setAscSort = !isReadyToRun || sort.order !== "asc"
    return <th>
      <div className="filter-toggle">
        <span onClick={() => this.sortGrid("ReadyToRun", setAscSort ? "asc" : "desc")} className="column-header-title text-uppercase mr-1">READY TO RUN</span>
        {isReadyToRun && <>
          <span hidden={sort.order !== "asc"}><FaArrowUp /></span>
          <span hidden={sort.order !== "desc"}><FaArrowDown /></span>
        </>}
        <ActionButton size="sm"
          className="btn-sm-icon p-0 mt-n1 ml-2"
          onClick={this.handleReadyToRunClick}
          spin={this.state.updatingReadyToRun}
          disableOnSpin><FiRefreshCw strokeWidth={3} /></ActionButton>
      </div>
    </th>
  }

  renderRows = () => {
    if (this.state.data?.Records && this.state.data?.Records.length) {
      return this.state.data.Records.map((row: PoolGridRow, i: number) =>
        <tr key={i}>
          <td className="text-center">
            <Checkbox id={`${row.PoolID}_check1_${i}`}
              checked={this.state.selectedPrintRowIDs.includes(row.PoolID)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.changePrintRowSelection(row.PoolID, e.target.checked)} />
          </td>
          <td><Button variant="link" onClick={() => this.showDetailsModal(row)} className="p-0">{row.Name}</Button></td>
          <td>{row.ID}</td>
          <td>{row.SelectionInterval}</td>
          <td>
            {row.Selection}
            {row.LastSelectionStatus === 1 && (<span className="small text-muted ml-1">(Canceled)</span>)}
          </td>
          <td>{FormatDate(row.LastMemberImport)}</td>
          <td>{FormatDate(row.ReadyToRunDate)}</td>
          <td className="text-center">
            {row.RequireManualVerify &&
              <Checkbox id={`ready-${row.ID}`}
                key={row.ID}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleChangeReadyToRunChange(row.PoolID, e.target.checked)}
                checked={this.state.readyToRunChanges.find(item => item.PoolID === row.PoolID)?.ReadyToRun ?? row.ReadyToRun} />}
          </td>
        </tr>)
    } else {
      return <tr><td colSpan={8}>No Records Found</td></tr>
    }
  }

  showDetailsModal = (details: PoolGridRow) => this.setState({ detailsModal: true, details: details })
  hideDetailsModal = () => {
    this.setState({ detailsModal: false, details: undefined })
    this.reload()
  }
  showModal = () => this.setState({ modal: true })
  hideModal = () => this.setState({ modal: false })
  reload = () => this.page$.next({ ...this.page$.value, pageNumber: 1 })

  handleCreateSuccessful = () => {
    this.hideModal()
    this.reload()
  }

  handleReadyToRunClick = () => {
    if (!this.state.readyToRunChanges.length) {
      toast.error("Please change status of at least one record.")
      return
    }
    this.setState({ updatingReadyToRun: true })
    this.updateReadyToRunSubscription && this.updateReadyToRunSubscription.unsubscribe()
    this.updateReadyToRunSubscription = PoolReadyToRunUpdateObservable(this.state.readyToRunChanges).subscribe(
      result => {
        this.setState({ updatingReadyToRun: false })
        toast.success(result)
        this.reload()
      },
      error => {
        this.setState({ updatingReadyToRun: false })
        toast.error(error)
      }
    )
  }

  handleChangeReadyToRunChange = (poolID: number, checked: boolean) => {
    let changes = [...this.state.readyToRunChanges]

    const index = changes.findIndex(item => item.PoolID === poolID)
    if (index > -1) {
      changes.splice(index, 1);
    }
    const pool = this.state.data?.Records.find(item => item.PoolID === poolID)
    if (pool && pool.ReadyToRun !== checked) {
      changes.push({ PoolID: poolID, ReadyToRun: checked })
    }
    this.setState({ readyToRunChanges: changes })
  }

  onRun = () => {
    this.setState({ runPools: false, cancelPools: false })
    this.reload()
  }

  onExportClick = () => {
    this.setState({ loadingCsv: true })
    const value = this.page$.value
    this.csvSubscription && this.csvSubscription.unsubscribe()
    this.csvSubscription = PoolGridCsvGet(value.filters, value.search, !!this.props.isConsortium).subscribe(
      result => {
        this.setState({ loadingCsv: false })
        this.export(result)
      },
      error => {
        this.setState({ loadingCsv: false })
        toast.error(error)
      }
    )
  }

  downloadTemplate = () => {
    // const header = this.props.isPortalAdmin ? csvHeader : csvHeader.filter((item) => item.toUpperCase() !== "MANAGER")
    const csvData = new Blob([csvHeader.join(",") + "\r\n"], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, "import-list-template.csv")
  }

  export = (csv: string) => {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("Pools", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  render() {
    const allPrintChecked = !this.state.loading && this.state.data?.Records?.length === this.state.selectedPrintRowIDs.length
    const { runPools, cancelPools } = this.state
    const poolsData = this.state.data?.AllPools
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <RandomNav canManagePools={this.props.canManagePools} canManagePersonnel={this.props.canManagePersonnel} canManageConsortiumPools={this.props.canManageConsortiumPools} isConsortium={!!this.props.isConsortium} />
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col>
              {this.state.detailsModal && this.state.details && <PoolDetailsModal isConsortium={!!this.props.isConsortium} details={this.state.details} onHide={this.hideDetailsModal} />}
              {this.state.modal && <AddPoolModal show={this.state.modal} IsConsortium={!!this.props.isConsortium} onHide={this.hideModal} onSuccess={this.handleCreateSuccessful} />}
              <SearchBar onSearch={this.onSearch} searchFields={DefaultSearchFields} />
            </Col>
            <Col md={6} className="text-md-right mt-2 mt-md-0">
              {this.props.isPortalAdmin && <ActionButton variant="secondary" size="sm" onClick={() => this.setState({ runPools: true })} spin={this.state.loading} disableOnSpin className="mr-2">{ this.props.isConsortium ? "RUN CONSORTIUMS" : "RUN POOLS" }</ActionButton>}
              {false && this.props.isPortalAdmin && <ActionButton variant="secondary" size="sm" onClick={() => this.setState({ cancelPools: true })} spin={this.state.loading} disableOnSpin className="mr-2">{ this.props.isConsortium ? "CANCEL CONSORTIUMS RUN" : "CANCEL POOLS RUN" }</ActionButton>}
              {!this.props.isConsortium && <Button variant="secondary" size="sm" onClick={this.showModal} className="mr-2">CREATE NEW POOL</Button>}
              {this.props.isConsortium && this.props.canManageConsortiumPools && this.props.isPortalAdmin && <Button variant="secondary" size="sm" onClick={this.showModal} className="mr-2">CREATE NEW CONSORTIUM</Button>}
              {!this.props.isConsortium && <Button variant="secondary" size="sm" className="mr-2" onClick={() => this.downloadTemplate()}>DOWNLOAD TEMPLATE</Button>}
              <ActionButton variant="secondary" size="sm" onClick={this.onExportClick} disabled={!(this.state.data?.Records || []).length} spin={this.state.loadingCsv} disableOnSpin>EXPORT CSV</ActionButton>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th className="text-center print-column">
                      <Checkbox id="print_all" checked={allPrintChecked} onChange={this.handlePrintSelectAllRowsChange} />
                      <Button variant="secondary" size="sm" onClick={() => this.handlePrintClick()} className="float-right" disabled={this.state.selectedPrintRowIDs.length === 0}><FaPrint /></Button>
                    </th>
                    {this.renderHeaderColumn("Name", this.props.isConsortium ? "Consortium Name" : "Pool Name")}
                    {this.renderHeaderColumn("ID", "Pool ID")}
                    {this.renderHeaderColumn("SelectionInterval", "Selection Interval")}
                    {this.renderHeaderColumn("LastSelection", "Last Selection")}
                    {this.renderHeaderColumn("LastUpdate", "Last Update")}
                    {this.renderHeaderColumn("ReadyToRunDate", "Date Ready To Run")}
                    {this.renderReadyToRunHeaderColumn()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={8}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data ?
            <Row>
              <Col>
                <span className="total-records">Total records: {this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />
              </Col>
            </Row> : undefined}
        </Container>
        { (runPools || cancelPools) && poolsData && <RunCancelModal isConsortium={!!this.props.isConsortium} poolsData={poolsData} cancel={cancelPools} onClose={()=> this.setState({ runPools: false, cancelPools: false })} onUpdate={this.onRun} />}
      </AppPageWrapper>
    )
  }
}
function mapStateToProps(state: AppState): RandomPoolsProps {
  const userAccess = BuildUserAccess(state)
  return {
    canManagePools: userAccess.canManagePools,
    canManagePersonnel: userAccess.canManagePersonnel,
    canManageConsortiumPools: userAccess.CanManageConsortiumPools,
    isPortalAdmin: userAccess.isPortalAdmin,
  }
}
const ConnectedRandomPools = connect(mapStateToProps, null)(RandomPools)
export default ConnectedRandomPools
